// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Inline destructive-action confirm. Replaces window.confirm() with a
// small popover anchored to the right of the trigger button. The popover
// renders via portal into document.body so it isn't constrained by the
// sidebar's overflow/scroll behavior — without the portal, native
// focus-into-view scrolling on the confirm button would drag the entire
// sidebar nav sideways to bring the off-sidebar popover into frame.
const ConfirmDeleteButton = ({
  onConfirm,
  trigger,
  triggerClassName,
  triggerTitle,
  question = 'Delete?',
  confirmLabel = 'Delete',
}) => {
  const [armed, setArmed] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const close = useCallback(() => setArmed(false), []);

  const open = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
    setArmed(true);
  }, []);

  useEffect(() => {
    if (!armed) return;
    const onDoc = (e) => {
      const pop = popoverRef.current;
      const trig = triggerRef.current;
      if (
        (pop && pop.contains(e.target)) ||
        (trig && trig.contains(e.target))
      ) {
        return;
      }
      close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    // Any scroll or resize invalidates the cached trigger position; just
    // close rather than re-measure to keep the interaction lightweight.
    const onScrollOrResize = () => close();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [armed, close]);

  // Focus the confirm button (so Enter confirms) without letting the
  // browser scroll its nearest scrollable ancestor — that's what was
  // shifting the sidebar.
  useEffect(() => {
    if (armed && confirmBtnRef.current) {
      confirmBtnRef.current.focus({ preventScroll: true });
    }
  }, [armed]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        title={triggerTitle}
        aria-haspopup="dialog"
        aria-expanded={armed}
        onClick={(e) => {
          e.stopPropagation();
          if (armed) close();
          else open();
        }}
      >
        {trigger}
      </button>
      {createPortal(
        <AnimatePresence>
          {armed && position && (
            <motion.div
              ref={popoverRef}
              className="confirm-delete-popover"
              style={{ top: position.top, left: position.left }}
              initial={{ opacity: 0, scale: 0.85, x: -6, y: '-50%' }}
              animate={{ opacity: 1, scale: 1, x: 0, y: '-50%' }}
              exit={{ opacity: 0, scale: 0.92, x: -4, y: '-50%' }}
              transition={{
                type: 'spring',
                stiffness: 420,
                damping: 32,
                mass: 0.6,
                opacity: { duration: 0.18, ease: [0.22, 1, 0.36, 1] },
              }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={question}
            >
              <span className="confirm-delete-question">{question}</span>
              <div className="confirm-delete-actions">
                <button
                  type="button"
                  className="confirm-delete-btn cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                  }}
                >
                  Cancel
                </button>
                <button
                  ref={confirmBtnRef}
                  type="button"
                  className="confirm-delete-btn confirm"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    onConfirm();
                  }}
                >
                  {confirmLabel}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default ConfirmDeleteButton;
